import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Settings, ShoppingCart, DollarSign, Package, Users, FileText,
  ClipboardList, PackageCheck, RotateCcw, Gavel, GitCompare, Wallet, Eye,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../core/contexts/SettingsContext';
import localApiAdapter from '../../core/services/localApiAdapter';
import BrandCurrencySelect from '../../shared/components/BrandCurrencySelect';
import ModuleConfigPageLayout from '../../shared/components/settings/ModuleConfigPageLayout';
import { getBrandVatRate } from '../../shared/utils/brandCurrency';
import { DEFAULT_ACHATS_CONFIG, normalizeAchatsConfig } from './achatsConfigDefaults';
import { syncAchatsConfigToSettings } from './useAchatsConfig';

const NS = 'modules.achats.pages.achatsConfigPage';

function ConfigSection({ icon: Icon, title, hint, children }) {
  return (
    <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
      <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2">
        <Icon className="h-6 w-6 shrink-0" />
        {title}
      </h2>
      {hint ? <p className="text-white/50 text-sm mb-4 text-right">{hint}</p> : <div className="mb-4" />}
      {children}
    </div>
  );
}

function ConfigCheckbox({ checked, onChange, label }) {
  return (
    <div className="flex items-center">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
      />
      <label className="ml-3 text-white/80">{label}</label>
    </div>
  );
}

function ConfigField({ label, hint, children }) {
  return (
    <div>
      <label className="block w-full text-sm font-medium text-white/70 mb-2 text-right">{label}</label>
      {children}
      {hint ? <p className="text-white/50 text-xs mt-1 text-right">{hint}</p> : null}
    </div>
  );
}

const inputClass = 'w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500';

const AchatsConfigPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const { patchSettings, settings } = useSettings();
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const [achatsConfig, setAchatsConfig] = useState(DEFAULT_ACHATS_CONFIG);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await localApiAdapter.get('/achats/config');
      const raw = response?.data !== undefined ? response.data : response;
      const merged = normalizeAchatsConfig(raw || {});
      setAchatsConfig(merged);
      syncAchatsConfigToSettings(merged, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error loading achats config:', error);
      const fallback = normalizeAchatsConfig({});
      setAchatsConfig(fallback);
      syncAchatsConfigToSettings(fallback, patchSettings, settingsRef.current);
    } finally {
      setLoading(false);
    }
  }, [patchSettings]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const payload = normalizeAchatsConfig(achatsConfig);
      await localApiAdapter.put('/achats/config', payload);
      syncAchatsConfigToSettings(payload, patchSettings, settingsRef.current);
      setAchatsConfig(payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving achats config:', error);
      setSaveError(p('save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(p('reset_confirm'))) return;
    try {
      const resetConfig = normalizeAchatsConfig({});
      setAchatsConfig(resetConfig);
      await localApiAdapter.put('/achats/config', resetConfig);
      syncAchatsConfigToSettings(resetConfig, patchSettings, settingsRef.current);
    } catch (error) {
      console.error('Error resetting achats config:', error);
      alert(p('reset_error'));
    }
  };

  const updateConfig = (key, value) => {
    setAchatsConfig((prev) => {
      const next = normalizeAchatsConfig({ ...prev, [key]: value });
      syncAchatsConfigToSettings(next, patchSettings, settingsRef.current);
      return next;
    });
  };

  const generalTab = (
    <div className="space-y-6">
      <ConfigSection icon={DollarSign} title={p('section_general')} hint={p('section_general_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('label_default_currency')} hint={p('label_default_currency_hint')}>
            <BrandCurrencySelect
              value={achatsConfig.defaultCurrency}
              onChange={(e) => updateConfig('defaultCurrency', e.target.value)}
            />
          </ConfigField>
          <ConfigField label={p('label_payment_terms')} hint={p('label_payment_terms_hint')}>
            <input
              type="text"
              value={achatsConfig.defaultPaymentTerms}
              onChange={(e) => updateConfig('defaultPaymentTerms', e.target.value)}
              className={inputClass}
            />
          </ConfigField>
          <ConfigField label={p('label_lead_time')} hint={p('label_lead_time_hint')}>
            <input
              type="number"
              min={0}
              value={achatsConfig.defaultLeadTimeDays}
              onChange={(e) => updateConfig('defaultLeadTimeDays', parseInt(e.target.value, 10) || 0)}
              className={inputClass}
            />
          </ConfigField>
          <ConfigField label={p('label_vat_rate')} hint={p('label_vat_rate_hint', { rate: getBrandVatRate() })}>
            <input
              type="text"
              value={`${achatsConfig.defaultVatRate ?? getBrandVatRate()}%`}
              disabled
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white/60 cursor-not-allowed"
            />
          </ConfigField>
        </div>
      </ConfigSection>

      <ConfigSection icon={Eye} title={p('section_display')} hint={p('section_display_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('items_per_page')} hint={p('items_per_page_hint')}>
            <select
              value={achatsConfig.itemsPerPage}
              onChange={(e) => updateConfig('itemsPerPage', parseInt(e.target.value, 10))}
              className={inputClass}
            >
              {[25, 50, 100].map((n) => (
                <option key={n} value={n} className="text-gray-900">{n}</option>
              ))}
            </select>
          </ConfigField>
          <ConfigField label={p('default_view')} hint={p('default_view_hint')}>
            <select
              value={achatsConfig.defaultView}
              onChange={(e) => updateConfig('defaultView', e.target.value)}
              className={inputClass}
            >
              <option value="dashboard" className="text-gray-900">{p('view_dashboard')}</option>
              <option value="table" className="text-gray-900">{p('view_table')}</option>
            </select>
          </ConfigField>
          <ConfigCheckbox
            checked={achatsConfig.showSupplierRatings}
            onChange={(v) => updateConfig('showSupplierRatings', v)}
            label={p('check_supplier_ratings')}
          />
          <ConfigCheckbox
            checked={achatsConfig.lowStockAlertEnabled}
            onChange={(v) => updateConfig('lowStockAlertEnabled', v)}
            label={p('check_low_stock_alert')}
          />
        </div>
      </ConfigSection>
    </div>
  );

  const suppliersTab = (
    <ConfigSection icon={Users} title={p('section_suppliers')} hint={p('section_suppliers_hint')}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConfigField label={p('supplier_catalog_prefix')} hint={p('supplier_catalog_prefix_hint')}>
          <input
            type="text"
            value={achatsConfig.supplierCatalogReferencePrefix}
            onChange={(e) => updateConfig('supplierCatalogReferencePrefix', e.target.value.toUpperCase())}
            className={inputClass}
          />
        </ConfigField>
        <div className="flex flex-col gap-3 justify-center">
          <ConfigCheckbox
            checked={achatsConfig.enableSupplierCatalog}
            onChange={(v) => updateConfig('enableSupplierCatalog', v)}
            label={p('enable_supplier_catalog')}
          />
          <ConfigCheckbox
            checked={achatsConfig.autoGenerateSupplierCatalogReference}
            onChange={(v) => updateConfig('autoGenerateSupplierCatalogReference', v)}
            label={p('auto_generate_supplier_catalog_reference')}
          />
        </div>
      </div>
    </ConfigSection>
  );

  const requestsOrdersTab = (
    <div className="space-y-6">
      <ConfigSection icon={FileText} title={p('section_requests')} hint={p('section_requests_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('request_reference_prefix')} hint={p('request_reference_prefix_hint')}>
            <input
              type="text"
              value={achatsConfig.requestReferencePrefix}
              onChange={(e) => updateConfig('requestReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={achatsConfig.enablePurchaseRequests}
              onChange={(v) => updateConfig('enablePurchaseRequests', v)}
              label={p('enable_purchase_requests')}
            />
            <ConfigCheckbox
              checked={achatsConfig.autoGenerateRequestNumber}
              onChange={(v) => updateConfig('autoGenerateRequestNumber', v)}
              label={p('check_auto_request_number')}
            />
            <ConfigCheckbox
              checked={achatsConfig.requireApprovalForRequests}
              onChange={(v) => updateConfig('requireApprovalForRequests', v)}
              label={p('check_approval_requests')}
            />
          </div>
        </div>
      </ConfigSection>

      <ConfigSection icon={ClipboardList} title={p('section_orders')} hint={p('section_orders_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('order_reference_prefix')} hint={p('order_reference_prefix_hint')}>
            <input
              type="text"
              value={achatsConfig.orderReferencePrefix}
              onChange={(e) => updateConfig('orderReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={achatsConfig.enablePurchaseOrders}
              onChange={(v) => updateConfig('enablePurchaseOrders', v)}
              label={p('enable_purchase_orders')}
            />
            <ConfigCheckbox
              checked={achatsConfig.autoGenerateOrderNumber}
              onChange={(v) => updateConfig('autoGenerateOrderNumber', v)}
              label={p('check_auto_order_number')}
            />
            <ConfigCheckbox
              checked={achatsConfig.requireApprovalForOrders}
              onChange={(v) => updateConfig('requireApprovalForOrders', v)}
              label={p('check_approval_orders')}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  const receiptsReturnsTab = (
    <div className="space-y-6">
      <ConfigSection icon={PackageCheck} title={p('section_receipts')} hint={p('section_receipts_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('receipt_reference_prefix')} hint={p('receipt_reference_prefix_hint')}>
            <input
              type="text"
              value={achatsConfig.receiptReferencePrefix}
              onChange={(e) => updateConfig('receiptReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={achatsConfig.enablePurchaseReceipts}
              onChange={(v) => updateConfig('enablePurchaseReceipts', v)}
              label={p('enable_purchase_receipts')}
            />
            <ConfigCheckbox
              checked={achatsConfig.autoGenerateReceiptNumber}
              onChange={(v) => updateConfig('autoGenerateReceiptNumber', v)}
              label={p('check_auto_receipt_number')}
            />
            <ConfigCheckbox
              checked={achatsConfig.allowPartialReceipts}
              onChange={(v) => updateConfig('allowPartialReceipts', v)}
              label={p('check_partial_receipts')}
            />
            <ConfigCheckbox
              checked={achatsConfig.qualityCheckRequired}
              onChange={(v) => updateConfig('qualityCheckRequired', v)}
              label={p('check_quality_control')}
            />
          </div>
        </div>
      </ConfigSection>

      <ConfigSection icon={RotateCcw} title={p('section_returns')} hint={p('section_returns_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('return_reference_prefix')} hint={p('return_reference_prefix_hint')}>
            <input
              type="text"
              value={achatsConfig.returnReferencePrefix}
              onChange={(e) => updateConfig('returnReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={achatsConfig.enablePurchaseReturns}
              onChange={(v) => updateConfig('enablePurchaseReturns', v)}
              label={p('enable_purchase_returns')}
            />
            <ConfigCheckbox
              checked={achatsConfig.autoGenerateReturnReference}
              onChange={(v) => updateConfig('autoGenerateReturnReference', v)}
              label={p('auto_generate_return_reference')}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  const tendersTab = (
    <div className="space-y-6">
      <ConfigSection icon={Gavel} title={p('section_tenders')} hint={p('section_tenders_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('tender_reference_prefix')} hint={p('tender_reference_prefix_hint')}>
            <input
              type="text"
              value={achatsConfig.tenderReferencePrefix}
              onChange={(e) => updateConfig('tenderReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={achatsConfig.enableTenders}
              onChange={(v) => updateConfig('enableTenders', v)}
              label={p('enable_tenders')}
            />
            <ConfigCheckbox
              checked={achatsConfig.autoGenerateTenderReference}
              onChange={(v) => updateConfig('autoGenerateTenderReference', v)}
              label={p('auto_generate_tender_reference')}
            />
          </div>
        </div>
      </ConfigSection>

      <ConfigSection icon={GitCompare} title={p('section_quote_comparison')} hint={p('section_quote_comparison_hint')}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfigField label={p('quote_comparison_prefix')} hint={p('quote_comparison_prefix_hint')}>
            <input
              type="text"
              value={achatsConfig.quoteComparisonReferencePrefix}
              onChange={(e) => updateConfig('quoteComparisonReferencePrefix', e.target.value.toUpperCase())}
              className={inputClass}
            />
          </ConfigField>
          <div className="flex flex-col gap-3 justify-center">
            <ConfigCheckbox
              checked={achatsConfig.enableQuoteComparison}
              onChange={(v) => updateConfig('enableQuoteComparison', v)}
              label={p('enable_quote_comparison')}
            />
            <ConfigCheckbox
              checked={achatsConfig.autoGenerateQuoteComparisonReference}
              onChange={(v) => updateConfig('autoGenerateQuoteComparisonReference', v)}
              label={p('auto_generate_quote_comparison_reference')}
            />
          </div>
        </div>
      </ConfigSection>
    </div>
  );

  const budgetTab = (
    <ConfigSection icon={Wallet} title={p('section_budget')} hint={p('section_budget_hint')}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <ConfigField label={p('budget_reference_prefix')} hint={p('budget_reference_prefix_hint')}>
          <input
            type="text"
            value={achatsConfig.budgetReferencePrefix}
            onChange={(e) => updateConfig('budgetReferencePrefix', e.target.value.toUpperCase())}
            className={inputClass}
          />
        </ConfigField>
        <ConfigField label={p('label_budget_threshold')} hint={p('label_budget_threshold_hint')}>
          <input
            type="number"
            min={0}
            max={100}
            value={achatsConfig.budgetAlertThreshold}
            onChange={(e) => updateConfig('budgetAlertThreshold', parseInt(e.target.value, 10) || 0)}
            className={inputClass}
          />
        </ConfigField>
        <div className="flex flex-col gap-3 justify-center">
          <ConfigCheckbox
            checked={achatsConfig.enableBudgetLink}
            onChange={(v) => updateConfig('enableBudgetLink', v)}
            label={p('enable_budget_link')}
          />
          <ConfigCheckbox
            checked={achatsConfig.autoGenerateBudgetReference}
            onChange={(v) => updateConfig('autoGenerateBudgetReference', v)}
            label={p('auto_generate_budget_reference')}
          />
        </div>
      </div>
    </ConfigSection>
  );

  const tabs = [
    { id: 'general', label: p('tab_general'), icon: DollarSign },
    { id: 'suppliers', label: p('tab_suppliers'), icon: Users },
    { id: 'requests_orders', label: p('tab_requests_orders'), icon: ShoppingCart },
    { id: 'receipts_returns', label: p('tab_receipts_returns'), icon: Package },
    { id: 'tenders', label: p('tab_tenders'), icon: Gavel },
    { id: 'budget', label: p('tab_budget'), icon: Wallet },
  ];

  const tabContent = {
    general: generalTab,
    suppliers: suppliersTab,
    requests_orders: requestsOrdersTab,
    receipts_returns: receiptsReturnsTab,
    tenders: tendersTab,
    budget: budgetTab,
  };

  return (
    <ModuleConfigPageLayout
      title={p('title')}
      subtitle={p('subtitle')}
      titleIcon={Settings}
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      tabContent={tabContent}
      loading={loading}
      saving={saving}
      saved={saved}
      savedMessage={p('saved_success')}
      saveError={saveError}
      onSave={handleSave}
      onReset={handleReset}
    />
  );
};

export default AchatsConfigPage;
